/* P&L form — the job's Project Profitability form, laid out like the QuickBooks
   report the office prints, with the people who get paid on the job.
   Every field is typed in by the team member; totals, gross profit and net
   income add themselves up. Print / Save as PDF prints just the sheet (one
   letter page). Used by the P&L Sheet page (blank, nothing saved) and by the
   P&L tab on Add / Update Record (filled from the record, saved with it).

   FINANCING = lender / dealer fees.
   MARKETING = canvassers and telemarketers (about 2% of the sale each role,
               shared by the split %, e.g. two telemarketers 50/50) plus the
               crew chief's override (a % of the sale, typed in).
   COMMISSIONS (expenses) = sales reps. If the job has no profit left for a
               regular commission, the rep gets a "mini" ($50–$500).          */

// ── Numbers to change here ──────────────────────────────────
const MARKETING_RATE = 2;        // % of the sale for canvassers, and again for telemarketers
const MINI_MIN = 50, MINI_MAX = 500;
// ────────────────────────────────────────────────────────────

const ROWS = { income: 1, subs: 3, supplies: 3, finance: 2, hourly: 1 };

const num = v => { const x = parseFloat(String(v ?? '').replace(/[$,%\s]/g, '')); return Number.isFinite(x) ? x : 0; };
const money = v => (v < 0 ? '−$' : '$') + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt2 = v => num(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyRows = k => Array.from({ length: ROWS[k] }, () => ({ name: '', amount: '' }));
export const blankPL = () => ({
  customer: '', job: '', contract: '', dates: 'All Dates',
  income: emptyRows('income'), subs: emptyRows('subs'), supplies: emptyRows('supplies'),
  finance: emptyRows('finance'), hourly: emptyRows('hourly'),
  canvRate: String(MARKETING_RATE), teleRate: String(MARKETING_RATE),
  mkt: [
    { role: 'Canvasser 1', group: 'canv', name: '', split: '100' },
    { role: 'Canvasser 2', group: 'canv', name: '', split: '' },
    { role: 'Telemarketer 1', group: 'tele', name: '', split: '100' },
    { role: 'Telemarketer 2', group: 'tele', name: '', split: '' },
  ],
  chief: { name: '', pct: '' },
  comm: ['Sales Rep 1', 'Sales Rep 2', 'Other'].map(role => ({ role, name: '', amount: '', mini: false })),
  preparedBy: '', date: '',
});

// Rows padded to the usual count so the sheet keeps its shape
const pad = (rows, k) => [...rows, ...emptyRows(k)].slice(0, Math.max(ROWS[k], rows.length));
const money2 = v => (num(v) ? fmt2(v) : '');
const SVC = { Roof: 'Roofing', EC: 'Exterior Coating', Win: 'Windows', Gut: 'Gutters' };

// A P&L filled in from a job record: sale price, contractor invoice, pre-paid bills,
// dealer fees, canvassers / telemarketers with their splits, and the sales reps.
// Everything stays editable; commissions for reps are left for the office to enter.
export function plFromJob(job) {
  const f = blankPL();
  if (!job) return f;
  const sale = num(job.change_order_amt) || num(job.contract_amt);   // Change Order Amount = the job's new total
  f.customer = job.customer_name || '';
  f.job = job.po_number || '';
  f.contract = sale ? '$' + sale.toLocaleString('en-US') : '';
  f.income = pad(sale ? [{ name: 'Service Income', amount: money2(sale) }] : [], 'income');
  const cons = [['con_roof', 'Roof'], ['con_ec', 'EC'], ['con_win', 'Win'], ['con_gut', 'Gut']].filter(([k]) => job[k]).map(([k]) => job[k]);
  const subs = [];
  if (num(job.contractor_invoice_amt) || cons.length) subs.push({ name: cons.join(', ') || 'Contractor', amount: money2(job.contractor_invoice_amt) });
  f.subs = pad(subs, 'subs');
  f.supplies = pad(num(job.prepaid_bills) ? [{ name: job.prepaid_notes || 'Pre-paid bills / materials', amount: money2(job.prepaid_bills) }] : [], 'supplies');
  const loans = (job.lender_payments || []).filter(l => num(l.dealer_fee) && !['No Go', 'Cancelled'].includes(l.status));
  f.finance = pad(loans.map(l => ({ name: `${l.lender_name || 'Lender'} fee${l.service ? ' — ' + (SVC[l.service] || l.service) : ''}`, amount: money2(l.dealer_fee) })), 'finance');
  const split = (v, other) => (v !== undefined && v !== null && String(v).trim() !== '' ? String(v) : (other ? '50' : '100'));
  f.mkt = [
    { role: 'Canvasser 1', group: 'canv', name: job.canvasser || '', split: job.canvasser ? split(job.canvasser_split, job.canvasser_2) : '100' },
    { role: 'Canvasser 2', group: 'canv', name: job.canvasser_2 || '', split: job.canvasser_2 ? split(job.canvasser_2_split, true) : '' },
    { role: 'Telemarketer 1', group: 'tele', name: job.telemarketer || '', split: job.telemarketer ? split(job.telemarketer_split, job.telemarketer_2) : '100' },
    { role: 'Telemarketer 2', group: 'tele', name: job.telemarketer_2 || '', split: job.telemarketer_2 ? split(job.telemarketer_2_split, true) : '' },
  ];
  f.comm[0].name = job.sales_rep || '';
  f.comm[1].name = job.sales_rep_2 || '';
  return f;
}

const CSS = `
.pl-bar{display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-bottom:16px}
.pl-btn{border:1px solid #0D3B2E;background:#0D3B2E;color:#fff;border-radius:10px;padding:9px 16px;font-weight:700;font-size:13px;cursor:pointer}
.pl-btn.alt{background:#fff;color:#0D3B2E}
.pl-stage{overflow-x:auto;padding-bottom:20px}
.pl-sheet{width:8.5in;min-height:11in;box-sizing:border-box;padding:.35in .55in;margin:0 auto;background:#fff;color:#222;
  box-shadow:0 4px 24px rgba(0,0,0,.12);font-family:Arial,Helvetica,sans-serif;font-size:10px}
.pl-head{text-align:center;margin-bottom:6px}
.pl-head .co{font-size:17px;font-weight:700;color:#333}
.pl-head .for{font-size:11.5px;color:#444;margin-top:3px;display:flex;justify-content:center;align-items:baseline;gap:5px;flex-wrap:wrap}
.pl-head .dates{font-size:11px;color:#555;margin-top:2px}
.pl-in{border:none;border-bottom:1px solid #999;background:transparent;font:inherit;color:#0a2a5e;font-weight:700;padding:0 2px;outline:none;min-width:0}
.pl-in:focus{border-bottom-color:#0D3B2E;background:#F2F8F5}
.pl-in.n{text-align:right;width:100%}
.pl-in.p{text-align:right;width:38px}
.pl-head .pl-in{text-align:center}
table.pl{width:100%;border-collapse:collapse}
table.pl td{padding:1.5px 4px;border-bottom:1px solid #d4d4d4;vertical-align:bottom;height:14px}
table.pl td.amt{width:110px;text-align:right}
table.pl tr.tot td{font-weight:700;color:#222;border-bottom:1px solid #888}
table.pl tr.big td{font-weight:800;font-size:12.5px;border-top:1.5px solid #333;border-bottom:1.5px solid #333;background:#F5F7F6}
table.pl tr.th td{border-bottom:1.5px solid #555;font-weight:700;color:#333}
table.pl tr.sec td{color:#555;padding-top:3px}
table.pl tr.sec td .hint{color:#888;font-size:8.5px;margin-left:6px}
.pad1{padding-left:14px!important}.pad2{padding-left:26px!important}.pad3{padding-left:38px!important}
.pl-row-in{display:flex;gap:6px;align-items:flex-end}
.pl-row-in .pl-in.nm{flex:1}
.pl-pct{font-size:9px;color:#555;white-space:nowrap;display:flex;align-items:flex-end;gap:2px}
.pl-mini{font-size:9px;color:#555;white-space:nowrap;display:flex;align-items:center;gap:3px}
.pl-warn{color:#B23B2E;font-size:8.5px;font-weight:700;display:block;text-align:right}
.pl-role{width:92px;color:#333;font-weight:700;white-space:nowrap}
.pl-calc{color:#0a2a5e;font-weight:700}
.pl-note{font-size:9px;color:#666;margin-top:5px;line-height:1.4}
.pl-sign{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:10px}
.pl-sign label{font-size:9.5px;color:#555;display:flex;gap:6px;align-items:flex-end}
.pl-sign .pl-in{flex:1}
.pl-foot{text-align:center;font-size:9px;color:#666;margin-top:8px}
.pl-neg{color:#B23B2E}
@media print{
  @page{size:letter portrait;margin:0}
  body *{visibility:hidden!important}
  .pl-sheet,.pl-sheet *{visibility:visible!important}
  .pl-stage{overflow:visible;padding:0}
  .pl-sheet{position:absolute;left:0;top:0;box-shadow:none;margin:0;height:11in;min-height:0;overflow:hidden}
  .pl-sheet,.pl-sheet *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .pl-in:focus{background:transparent}
  .pl-in::placeholder{color:#c4c4c4}
  html,body{background:#fff!important}
}
`;

// The sheet itself. `f` = the form values, `setF` = a React state setter for them.
export default function PLForm({ f, setF }) {

  const setTop = (k, v) => setF(s => ({ ...s, [k]: v }));
  const setRow = (sec, i, k, v) => setF(s => ({ ...s, [sec]: s[sec].map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));
  const setChief = (k, v) => setF(s => ({ ...s, chief: { ...s.chief, [k]: v } }));
  const tidy = (sec, i) => setF(s => ({ ...s, [sec]: s[sec].map((r, j) => (j !== i || !String(r.amount).trim() || !Number.isFinite(parseFloat(String(r.amount).replace(/[$,\s]/g, ''))) ? r : { ...r, amount: fmt2(r.amount) })) }));
  const sum = sec => f[sec].reduce((s, r) => s + num(r.amount), 0);

  const income = sum('income');
  const subs = sum('subs'), supplies = sum('supplies');
  const cogsCore = subs + supplies;
  const financing = sum('finance');

  // Marketing: rate% of the sale for each role, shared by the split
  const rateOf = g => num(g === 'canv' ? f.canvRate : f.teleRate) / 100;
  const mktAmt = r => (r.name.trim() || num(r.split) ? income * rateOf(r.group) * num(r.split) / 100 : 0);
  const splitTotal = g => f.mkt.filter(r => r.group === g && r.name.trim()).reduce((s, r) => s + num(r.split), 0);
  const splitOff = g => f.mkt.filter(r => r.group === g && r.name.trim()).length > 1 && Math.abs(splitTotal(g) - 100) > 0.01;
  const chiefAmt = income * num(f.chief.pct) / 100;
  const marketing = f.mkt.reduce((s, r) => s + mktAmt(r), 0) + chiefAmt;

  const cogs = cogsCore + financing + marketing;
  const gross = income - cogs;
  const hourly = sum('hourly');
  const commissions = sum('comm');
  const expenses = hourly + commissions;
  const net = gross - expenses;
  const any = income || cogs || expenses;

  const Amt = ({ v }) => <span className={v < 0 ? 'pl-neg' : ''}>{any ? money(v) : ''}</span>;
  const rowsOf = (sec, pad, placeholder) => f[sec].map((r, i) => (
    <tr key={sec + i}>
      <td className={pad}><input className="pl-in" style={{ width: '70%' }} placeholder={placeholder} value={r.name} onChange={e => setRow(sec, i, 'name', e.target.value)} /></td>
      <td className="amt"><input className="pl-in n" inputMode="decimal" placeholder="0.00" value={r.amount} onChange={e => setRow(sec, i, 'amount', e.target.value)} onBlur={() => tidy(sec, i)} /></td>
    </tr>
  ));

  return (
    <>
      <style>{CSS}</style>
        <div className="pl-stage">
          <div className="pl-sheet">
            <div className="pl-head">
              <div className="co">CPR Home Solutions, Inc</div>
              <div className="for">
                Project Profitability for
                <input className="pl-in" style={{ width: 170 }} placeholder="Customer name" value={f.customer} onChange={e => setTop('customer', e.target.value)} />
                <input className="pl-in" style={{ width: 95 }} placeholder="Job #" value={f.job} onChange={e => setTop('job', e.target.value)} />
                <input className="pl-in" style={{ width: 85 }} placeholder="$ amount" value={f.contract} onChange={e => setTop('contract', e.target.value)} />
              </div>
              <div className="dates"><input className="pl-in" style={{ width: 150 }} value={f.dates} onChange={e => setTop('dates', e.target.value)} /></div>
            </div>

            <table className="pl">
              <tbody>
                <tr className="th"><td></td><td className="amt">Total</td></tr>

                <tr className="sec"><td>Income</td><td className="amt"></td></tr>
                {rowsOf('income', 'pad1', 'Service Income')}
                <tr className="tot"><td>Total for Income</td><td className="amt"><Amt v={income} /></td></tr>

                <tr className="sec"><td>Cost of Goods Sold</td><td className="amt"></td></tr>
                <tr className="sec"><td className="pad1">COST OF GOODS (COGS)</td><td className="amt"></td></tr>
                <tr className="sec"><td className="pad2">Subcontractors</td><td className="amt"></td></tr>
                {rowsOf('subs', 'pad3', 'Subcontractor')}
                <tr className="tot"><td className="pad2">Total for Subcontractors</td><td className="amt"><Amt v={subs} /></td></tr>
                <tr className="sec"><td className="pad2">Supplies &amp; Materials</td><td className="amt"></td></tr>
                {rowsOf('supplies', 'pad3', 'Supplier')}
                <tr className="tot"><td className="pad2">Total for Supplies &amp; Materials</td><td className="amt"><Amt v={supplies} /></td></tr>
                <tr className="tot"><td className="pad1">Total for COST OF GOODS (COGS)</td><td className="amt"><Amt v={cogsCore} /></td></tr>

                <tr className="sec"><td className="pad1">FINANCING</td><td className="amt"></td></tr>
                <tr className="sec"><td className="pad2">Finance Fees</td><td className="amt"></td></tr>
                {rowsOf('finance', 'pad3', 'Lender / dealer fee (e.g. GoodLeap)')}
                <tr className="tot"><td className="pad1">Total for FINANCING</td><td className="amt"><Amt v={financing} /></td></tr>

                <tr className="sec"><td className="pad1">MARKETING<span className="hint">canvassers &amp; telemarketers: % of the sale × split · crew chief: override % of the sale</span></td><td className="amt"></td></tr>
                {f.mkt.map((r, i) => {
                  const first = i === 0 || f.mkt[i - 1].group !== r.group;
                  const rateKey = r.group === 'canv' ? 'canvRate' : 'teleRate';
                  return (
                    <tr key={'m' + i}>
                      <td className="pad2">
                        <div className="pl-row-in">
                          <span className="pl-role">{r.role}</span>
                          <input className="pl-in nm" placeholder="Name" value={r.name} onChange={e => setRow('mkt', i, 'name', e.target.value)} />
                          <span className="pl-pct">split <input className="pl-in p" inputMode="decimal" placeholder="0" value={r.split} onChange={e => setRow('mkt', i, 'split', e.target.value)} />%</span>
                          {first
                            ? <span className="pl-pct">rate <input className="pl-in p" inputMode="decimal" value={f[rateKey]} onChange={e => setTop(rateKey, e.target.value)} />%</span>
                            : <span className="pl-pct" style={{ width: 66 }} />}
                        </div>
                      </td>
                      <td className="amt">
                        <span className="pl-calc">{any && mktAmt(r) ? money(mktAmt(r)) : ''}</span>
                        {!first && splitOff(r.group) && <span className="pl-warn">Splits add to {splitTotal(r.group)}%, not 100%</span>}
                      </td>
                    </tr>
                  );
                })}
                <tr>
                  <td className="pad2">
                    <div className="pl-row-in">
                      <span className="pl-role">Crew Chief</span>
                      <input className="pl-in nm" placeholder="Name" value={f.chief.name} onChange={e => setChief('name', e.target.value)} />
                      <span className="pl-pct">override <input className="pl-in p" inputMode="decimal" placeholder="0" value={f.chief.pct} onChange={e => setChief('pct', e.target.value)} />% of the sale</span>
                    </div>
                  </td>
                  <td className="amt"><span className="pl-calc">{any && chiefAmt ? money(chiefAmt) : ''}</span></td>
                </tr>
                <tr className="tot"><td className="pad1">Total for MARKETING</td><td className="amt"><Amt v={marketing} /></td></tr>
                <tr className="tot"><td>Total for Cost of Goods Sold</td><td className="amt"><Amt v={cogs} /></td></tr>

                <tr className="big"><td>Gross Profit</td><td className="amt"><Amt v={gross} /></td></tr>

                <tr className="sec"><td>Expenses</td><td className="amt"></td></tr>
                {rowsOf('hourly', 'pad1', 'Hourly Cost')}
                <tr className="sec"><td className="pad1">Sales Commissions</td><td className="amt"></td></tr>
                {f.comm.map((r, i) => {
                  const a = num(r.amount);
                  const out = r.mini && a && (a < MINI_MIN || a > MINI_MAX);
                  return (
                    <tr key={'c' + i}>
                      <td className="pad2">
                        <div className="pl-row-in">
                          <span className="pl-role">{r.role}</span>
                          <input className="pl-in nm" placeholder="Name" value={r.name} onChange={e => setRow('comm', i, 'name', e.target.value)} />
                          <label className="pl-mini"><input type="checkbox" checked={r.mini} onChange={e => setRow('comm', i, 'mini', e.target.checked)} />Mini</label>
                        </div>
                      </td>
                      <td className="amt">
                        <input className="pl-in n" inputMode="decimal" placeholder="0.00" value={r.amount} onChange={e => setRow('comm', i, 'amount', e.target.value)} onBlur={() => tidy('comm', i)} />
                        {out && <span className="pl-warn">Mini is ${MINI_MIN}–${MINI_MAX}</span>}
                      </td>
                    </tr>
                  );
                })}
                <tr className="tot"><td className="pad1">Total for Sales Commissions</td><td className="amt"><Amt v={commissions} /></td></tr>
                <tr className="tot"><td>Total for Expenses</td><td className="amt"><Amt v={expenses} /></td></tr>

                <tr className="big"><td>Net Operating Income</td><td className="amt"><Amt v={net} /></td></tr>
                <tr className="big"><td>Net Income</td><td className="amt"><Amt v={net} /></td></tr>
              </tbody>
            </table>

            <div className="pl-note">
              Marketing: canvassers and telemarketers get about {MARKETING_RATE}% of the sale per role, shared by the split (e.g. two telemarketers 50/50); the crew chief gets an override % of the sale.
              Sales commissions: if the job has no profit left for a regular commission, pay a <b>mini</b> commission of ${MINI_MIN}–${MINI_MAX}, depending on what's left, and tick <b>Mini</b>.
            </div>

            <div className="pl-sign">
              <label>Prepared by <input className="pl-in" value={f.preparedBy} onChange={e => setTop('preparedBy', e.target.value)} /></label>
              <label>Date <input className="pl-in" value={f.date} onChange={e => setTop('date', e.target.value)} /></label>
            </div>
            <div className="pl-foot">Cash Basis</div>
          </div>
        </div>
    </>
  );
}
